export default defineAppConfig({
  ui: {
    alert: {
      slots: {
        close: 'justify-end',
      },
      compoundVariants: [
        {
          color: 'primary',
          variant: 'subtle',
          class: {
            root: 'bg-primary-100 text-gray-900 ring ring-inset ring-primary-300',
            title: 'font-bold',
            icon: 'text-primary-600',
          },
        },
      ],
    },
    container: {
      base: 'w-full max-w-(--ui-container) mx-auto px-4 sm:px-6 lg:px-8 xl:px-9',
    },
    select: {
      variants: {
        size: {
          xs: {
            base: 'h-7 text-xs',
          },
          sm: {
            base: 'h-8 text-xs',
          },
          md: {
            base: 'h-9 text-sm',
          },
          lg: {
            base: 'h-10 text-sm',
          },
          xl: {
            base: 'h-12 text-base',
          },
        },
      },
    },
    button: {
      slots: {
        base: 'font-bold disabled:opacity-50 aria-disabled:opacity-50',
      },
      variants: {
        variant: {
          link: 'underline',
        },
        size: {
          xs: {
            base: 'px-2.5 py-0 h-7 text-xs gap-1',
          },
          sm: {
            base: 'px-3 py-0 h-8 text-sm gap-2',
          },
          md: {
            base: 'px-4 py-0 h-9 text-sm gap-2',
          },
          lg: {
            base: 'px-4 py-0 h-10 text-base gap-2',
          },
          xl: {
            base: 'px-6 py-0 h-12 text-base gap-3',
            leadingIcon: 'size-5',
            trailingIcon: 'size-5',
          },
        },
      },
      compoundVariants: [
        {
          color: 'primary',
          variant: 'solid',
          class:
            'text-white bg-primary-900 hover:bg-primary-800 focus:outline-none focus-visible:bg-primary-800 disabled:bg-primary-800 aria-disabled:bg-primary-800 disabled:opacity-50 aria-disabled:opacity-50 active:bg-primary-700',
        },
        {
          color: 'primary',
          variant: 'soft',
          class:
            'text-primary-900 bg-primary-400 hover:bg-primary-300 focus:outline-none focus-visible:bg-primary-300 disabled:bg-primary-300 aria-disabled:bg-primary-300 disabled:opacity-50 aria-disabled:opacity-50 active:bg-primary-400',
        },
        {
          color: 'primary',
          variant: 'link',
          class: 'p-0 h-auto text-primary-600 hover:text-primary-500',
        },
        {
          color: 'neutral',
          variant: 'solid',
          class:
            'shadow-xs text-neutral-700 ring ring-neutral-300 shadow-neutral-300 bg-gray-50 hover:bg-gray-100 disabled:bg-gray-100 aria-disabled:bg-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-inverted active:bg-gray-50',
        },
        {
          color: 'neutral',
          variant: 'ghost',
          class:
            'text-default hover:bg-accented/75 focus:outline-none focus-visible:bg-accented/75 disabled:bg-elevated aria-disabled:bg-elevated',
        },
        {
          color: 'neutral',
          variant: 'link',
          class: 'p-0 h-auto text-muted hover:text-default',
        },
        {
          color: 'error',
          variant: 'link',
          class: 'p-0 h-auto text-red-700 hover:text-red-500',
        },
        {
          size: 'xs',
          square: true,
          class: 'p-1.5 w-7',
        },
        {
          size: 'sm',
          square: true,
          class: 'p-2 w-8',
        },
        {
          size: 'md',
          square: true,
          class: 'p-2 w-9',
        },
        {
          size: 'lg',
          square: true,
          class: 'p-2.5 w-10',
        },
        {
          size: 'xl',
          square: true,
          class: 'p-3.5 w-12',
        },
        {
          loading: true,
          leading: true,
          class: {
            leadingIcon: 'i-gg-spinner',
          },
        },
        {
          loading: true,
          leading: false,
          trailing: true,
          class: {
            trailingIcon: 'i-gg-spinner',
          },
        },
      ],
      defaultVariants: {
        color: 'primary',
        variant: 'soft',
        size: 'sm',
      },
    },
    input: {
      slots: {
        root: 'block',
      },
      variants: {
        buttonGroup: {
          horizontal: {
            root: 'group',
            base: 'group-not-only:group-first:rounded-e-none group-not-only:group-last:rounded-s-none group-not-last:group-not-first:rounded-none',
          },
          vertical: {
            root: 'group',
            base: 'group-not-only:group-first:rounded-b-none group-not-only:group-last:rounded-t-none group-not-last:group-not-first:rounded-none',
          },
        },
        size: {
          xs: {
            base: 'px-2 py-1 text-xs leading-4 h-7 gap-1',
          },
          sm: {
            base: 'px-2.5 py-1.5 text-xs leading-4 h-8 gap-2',
          },
          md: {
            base: 'px-2.5 py-1.5 text-sm leading-5 h-9 gap-2',
          },
          lg: {
            base: 'px-3 py-2 text-sm leading-6 h-10 gap-2',
          },
          xl: {
            base: 'px-3 py-2 text-base leading-6 h-12 gap-x-3',
          },
        },
      },
    },
    inputMenu: {
      slots: {
        root: 'block',
      },
      variants: {
        size: {
          xs: {
            base: 'px-2 py-1 text-xs leading-4 h-7 gap-1',
          },
          sm: {
            base: 'px-2.5 py-1.5 text-xs leading-4 h-8 gap-2',
          },
          md: {
            base: 'px-2.5 py-1.5 text-sm leading-5 h-9 gap-2',
          },
          lg: {
            base: 'px-3 py-2 text-sm leading-6 h-10 gap-2',
          },
          xl: {
            base: 'px-3 py-2 text-base leading-6 h-12 gap-x-3',
          },
        },
      },
    },
    textarea: {
      slots: {
        root: 'block',
      },
    },
    formField: {
      slots: {
        label: 'text-gray-700 truncate text-sm font-bold',
      },
    },
    modal: {
      slots: {
        footer: 'justify-center gap-4',
      },
    },
    card: {
      slots: {
        header: 'p-3 sm:p-5',
        body: 'p-3 sm:p-5',
        footer: 'p-3 sm:p-5',
      },
      variants: {
        variant: {
          outline: {
            root: 'bg-default ring ring-default divide-none',
          },
        },
      },
    },
    badge: {
      defaultVariants: {
        color: 'primary',
        variant: 'soft',
        size: 'md',
      },
    },

    carousel: {
      slots: {
        dots: 'bottom-4 gap-2',
        dot: ['size-2 bg-white opacity-50'],
      },
      variants: {
        orientation: {
          horizontal: {
            prev: 'start-4 sm:start-4 top-1/2 -translate-y-1/2',
            next: 'end-4 sm:end-4 top-1/2 -translate-y-1/2',
          },
        },

        active: {
          true: {
            dot: 'data-[state=active]:opacity-80 data-[state=active]:bg-white',
          },
        },
      },
    },

    pageSection: {
      slots: {
        title: 'font-[Quincy]',
      },
    },
    pageHero: {
      slots: {
        title: 'font-[Quincy]',
      },
    },
    pageCTA: {
      slots: {
        title: 'font-[Quincy]',
      },
    },
    pageGrid: {
      base: 'gap-4',
    },
  },
})
